import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import styled from 'styled-components'

import { useApi, usePersistedApi } from 'hooks'
import { entities } from 'reduxStore'
import { exitReact } from 'utils'
import Api from 'api'

import { PurchaseTokensFlow } from "components/PurchaseTokensFlow";
import Popup from 'components/Popup'
import WhatAreTokens from 'components/WhatAreTokens'
import FixedLoader from 'components/FixedLoader'
import FixedError from 'components/FixedError'
import { TopBarRedesigned } from "components/TopBarRedesigned";
import { JobBoard } from "components/JobBoard";
import { JobBoardModal } from "./components/JobBoardModal";
import { UserAccountProps } from "utils/types";

const PageWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: #fff;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for better mobile support */
  /* Ensure smooth scrolling on all devices */
  -webkit-overflow-scrolling: touch;
`;

interface LocationProps {
    openPopup?: boolean
}


export default function JobBoardPage() {
    const { state } = useLocation<LocationProps>();
    const openPopup = state?.openPopup ?? false;
    const [isOpenPopup, setIsOpenPopup] = useState(openPopup);
    const [jobs, jobsLoading, jobsError, getAllHireJobs] = usePersistedApi({
        apiMethod: Api.getAllHireJobs,
        entityName: entities.dentistJobs,
        initialValue: []
    })
    const [dentistUser, dentistUserLoading, dentistUserError, getDentistUser] = usePersistedApi({
        apiMethod: Api.getUser,
        entityName: entities.user,
        initialValue: {} as UserAccountProps,
    })
    const [billingLocations, locationsLoading, locationsError, getBillingLocations] = usePersistedApi({
        apiMethod: Api.getLocations,
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })
    useEffect(() => {
        getAllHireJobs({})
        getDentistUser({})
        getBillingLocations({})
    }, [getAllHireJobs, getDentistUser, getBillingLocations])

    const updateDentistUser = useApi({
        apiMethod: Api.updateUser,
        onSuccess: useCallback(() => getDentistUser({}), [getDentistUser]),
        initialValue: {},
    })[3]

    const history = useHistory()
    const [whatAreTokensOpened, setWhatAreTokensOpened] = useState(false)
    const [tokensPurchaseOpened, setTokensPurchaseOpened] = useState(false)

    useEffect(() => {
        getDentistUser({})
    }, [getDentistUser])

    useEffect(() => {
        if (
            dentistUser?.id &&
            !dentistUser.seenTokensInfo
        ) {
            if (!whatAreTokensOpened) {
                setWhatAreTokensOpened(true)
            }
        } else {
            if (whatAreTokensOpened) {
                setWhatAreTokensOpened(false)
            }
        }
    }, [dentistUser, whatAreTokensOpened, setWhatAreTokensOpened])

    return (
        <PageWrapper>
            <TopBarRedesigned
                onBack={() => {
                    history.push('loading')
                    exitReact('home')
                }}
            />
            <JobBoard
                jobs={jobs}
                dentistUserEmail={dentistUser.email || ''}
                tokensCount={dentistUser.TokenAmount || 0}
                onTokensClick={() => {
                    setTokensPurchaseOpened(!tokensPurchaseOpened)
                }}
            />
            <Popup
                opened={whatAreTokensOpened}
            >
                <WhatAreTokens
                    onClose={useCallback(() => {
                        updateDentistUser({
                            seenTokensInfo: 1,
                        })
                    }, [updateDentistUser])}
                    onGotIt={useCallback(() => {
                        updateDentistUser({
                            seenTokensInfo: 1,
                        })
                    }, [updateDentistUser])}
                />
            </Popup>
            {tokensPurchaseOpened ? (
                <PurchaseTokensFlow
                    onClose={() => {
                        setTokensPurchaseOpened(false)
                    }}
                    onSuccess={() => {
                        setTokensPurchaseOpened(false)
                        getDentistUser({})
                    }}
                />
            ) : null}
            <FixedLoader
                loading={
                    jobsLoading
                    || dentistUserLoading
                    || locationsLoading
                }
            />
            <JobBoardModal
                isOpen={isOpenPopup && !whatAreTokensOpened}
                title={"Job Board"}
                description={"The Job Board is only for finding, recruiting, and hiring permanent or long-term contract staff. Our Terms and Services strictly prevent use of this feature for finding temps. If you're looking for a great temporary hygienist or assistant, click the 'Get a Temp' button."}
                onClose={() => setIsOpenPopup(false)}
            />
            <FixedError
                error={
                    jobsError
                    || dentistUserError
                    || locationsError
                }
            />
        </PageWrapper>
    )
}
